import { useEffect, useState } from 'react';

export type ToastKind = 'success' | 'error' | 'info';
export interface ToastItem { id: number; kind: ToastKind; message: string }

const listeners = new Set<(t: ToastItem[]) => void>();
let toasts: ToastItem[] = [];
let nextId = 1;
function emit() { for (const l of listeners) l([...toasts]); }

const MUTE_KEY = 'wcfantasy.toasts';

function isMuted(kind: ToastKind): boolean {
  try {
    const v = localStorage.getItem(MUTE_KEY);
    if (v === 'off') return true;            // suppress everything
    if (v === 'errors-only') return kind !== 'error';
    return false;
  } catch { return false; }
}

export function showToast(kind: ToastKind, message: string, duration = 3200): void {
  if (isMuted(kind)) return;
  const id = nextId++;
  toasts = [...toasts, { id, kind, message }];
  emit();
  setTimeout(() => { toasts = toasts.filter((t) => t.id !== id); emit(); }, duration);
}

export const toast = {
  success: (m: string) => showToast('success', m),
  error: (m: string) => showToast('error', m),
  info: (m: string) => showToast('info', m),
};

function useToasts(): ToastItem[] {
  const [list, setList] = useState<ToastItem[]>(toasts);
  useEffect(() => {
    listeners.add(setList);
    return () => { listeners.delete(setList); };
  }, []);
  return list;
}

export default function ToastContainer() {
  const list = useToasts();
  if (list.length === 0) return null;
  return (
    <div
      className="fixed top-3 inset-x-2 z-[60] flex flex-col items-center gap-2 pointer-events-none"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      {list.map((t) => (
        <div
          key={t.id}
          role="status"
          aria-live="polite"
          className={`pointer-events-auto max-w-md w-full sm:w-auto card px-3.5 py-2.5 text-sm shadow-glow ring-1 animate-toast-in ${
            t.kind === 'success' ? 'ring-pitch-500/40 bg-pitch-500/10'
            : t.kind === 'error' ? 'ring-red-500/40 bg-red-500/10'
            : 'ring-ink-600 bg-ink-900'
          }`}
        >
          <div className={`${
            t.kind === 'success' ? 'text-pitch-400'
            : t.kind === 'error' ? 'text-red-400'
            : 'text-chalk'
          }`}>{t.message}</div>
        </div>
      ))}
    </div>
  );
}
